import React, { memo, useCallback } from 'react';
import { useContainerQuery } from 'react-container-query';
import classNames from 'classcat';
import { EffectTypes } from 'services/Effects';
import type { Offer } from 'lib/LobbyEntities';
import cfImage from 'lib/cfImage';
import withLazyStyle from 'components/LazyStyle';
import { Typography, TypographyVariant } from 'components/Typography';
import useOfferBannerAnalytics from './src/useOfferBannerAnalytics';
import { query, themeClassNameMap } from './constants';
import Belt from './src/Belt';
import style from './offerBanner.css?lazy';

interface OfferBannerProps {
  offer: Offer;
  source: EffectTypes.OfferClickedSourceType;
  onClick?: () => void;
  openModal?: () => void;
}

const OfferBanner = ({ offer, source, openModal, onClick }: OfferBannerProps) => {
  // Why does the root div have a onClick Handler?
  // The stakeholders of this feature want a click anywhere on the card to trigger the default action
  // Making the div have an onClick will give that behaviour, while buttons inside may have another
  // onClick functionality which will stop the onClick propagation (bubbling)

  const { label, title, subtitle, shortDescription, theme, lobbyImageURL, ctaText, ctaURL } = offer;
  const [params, containerRef] = useContainerQuery(query, { width: 0 });
  const handleOfferClicked = useOfferBannerAnalytics({ offer, source });
  const handleOfferBannerClick = useCallback(() => {
    onClick?.();
    openModal?.();
    handleOfferClicked({ interactionType: EffectTypes.OfferClickedInteractionType.CardClick });
  }, [openModal, onClick, handleOfferClicked]);

  const handleCtaClick = useCallback(() => {
    handleOfferClicked({
      interactionType: EffectTypes.OfferClickedInteractionType.PrimaryCTAClick
    });
  }, [handleOfferClicked]);

  const lobbyImageSrc = cfImage(lobbyImageURL, {
    width: 1024,
    height: 256
  });

  return (
    <section
      ref={containerRef}
      onClick={handleOfferBannerClick}
      className={classNames(['offerBanner', themeClassNameMap[theme], params])}
      style={{ backgroundImage: `url(${lobbyImageSrc})` }}
    >
      <div className="offerBanner__content">
        <Typography variant={TypographyVariant.BodyXxsStrong}>
          <span className="offerBanner__label">{label}</span>
        </Typography>
        <Typography variant={TypographyVariant.HeaderXs}>
          <span className="offerBanner__title">{title}</span>
        </Typography>
        <Typography variant={TypographyVariant.BodyXxs}>
          <span className="offerBanner__subtitle">{subtitle}</span>
        </Typography>
        <Typography variant={TypographyVariant.BodyXxs}>
          <span className="offerBanner__shortDescription">{shortDescription}</span>
        </Typography>
      </div>
      <Belt ctaText={ctaText} ctaUrl={ctaURL} onCtaClick={handleCtaClick} />
    </section>
  );
};

export default memo(withLazyStyle(style)(OfferBanner));
